import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import Icon from '../../elementals/icon'
import { colorVars } from '../../../constants/colorVars'
import cls from './styles.scss'

const cx = classnames.bind(cls)

/**
 * We may need to make this controlled if the surrounding context ever needs to reference whether the property is saved
 * anywhere else. From my initial searches it seems fine for this to be represented like this. Perhaps we keep it
 * uncontrolled and a callback. I don't think it's wise to allow these components to mutate the object without some
 * structured update pattern being defined by the app, who ultimately owns the data.
 */
const PropertySaveIndicator = ({
  elmId,
  property = {},
  onSaveToggle,
  isCancelled = false,
  whiteBackground = false,
  isSaved: isSavedProp,
}) => {
  // Controlled when `isSaved` is provided; otherwise falls back to the
  // property's own is_currently_saved. Lets consumers whose listing object is
  // not the saved-state source of truth (e.g. SRP/calendar reading a
  // useSavedProperties collection) drive the heart with a boolean — no need to
  // merge saved-state into the listing objects.
  const isSaved = isSavedProp ?? property.is_currently_saved
  const iconStyle = isSaved ? 'solid' : 'regular'

  let iconBaseColor
  let iconHoverColor

  if (whiteBackground) {
    iconBaseColor = colorVars.utkColorWhite
    iconHoverColor = null
  } else {
    iconBaseColor = isSaved ? colorVars.utkColorError : colorVars.utkColorWhite
    iconHoverColor = isSaved
      ? colorVars.utkColorError
      : colorVars.utkColorGrayExtraLight
  }

  return (
    <Icon
      color={iconBaseColor}
      className={cx(
        'save-indicator',
        { 'is-saved': isSaved },
        { 'white-background': whiteBackground },
        { disabled: isCancelled }
      )}
      data-elm-id={`save_property_icon_${elmId}`}
      hoverColor={iconHoverColor}
      name='heart'
      iconStyle={iconStyle}
      onClick={() => onSaveToggle(property)}
      size='large'
    />
  )
}

PropertySaveIndicator.propTypes = {
  elmId: PropTypes.string.isRequired,
  isSaved: PropTypes.bool,
  property: PropTypes.shape({
    property_id: PropTypes.string.isRequired,
    is_currently_saved: PropTypes.bool,
  }).isRequired,
  onSaveToggle: PropTypes.func,
  isCancelled: PropTypes.bool,
  whiteBackground: PropTypes.bool,
}

/**
 * NOTE: I have purposely not exported this generally yet so as to force the scope this change for the ListingCard for now.
 * I would expect us to need to export this before repipe is complete.
 */
export default PropertySaveIndicator
